'use client';
import * as React from 'react';
import { ThemeContext } from '../Theme/index.js';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

function Base(props) {
    var _a = props.checked, checked = _a === void 0 ? false : _a, _b = props.required, required = _b === void 0 ? false : _b, _c = props.disabled, disabled = _c === void 0 ? false : _c;
    var handleInput = function (e) {
        props.onInput && props.onInput(e);
    };
    var handleChange = function (e) {
        props.onChange && props.onChange(e);
    };
    var handleBlur = function (e) {
        props.onBlur && props.onBlur(e);
    };
    return (React.createElement("input", { type: props.type, id: props.id, name: props.name, placeholder: props.placeholder, style: props.style, className: props.className, defaultValue: props.defaultValue, value: props.value, checked: checked, required: required, disabled: disabled, onInput: handleInput, onChange: handleChange, onBlur: handleBlur }));
}

var CheckTheme;
(function (CheckTheme) {
    CheckTheme["ACSMARINE"] = "acsm";
})(CheckTheme || (CheckTheme = {}));

var themes$1 = new Map();
var defaultTheme$1 = new Map();
defaultTheme$1.set("element", "rounded ease-out duration-500 bg-gray-900 accent-gray-900 disabled:bg-gray-400");
defaultTheme$1.set("container", "w-fit");
defaultTheme$1.set("label", "ml-2 md:ml-3");
defaultTheme$1.set("disabled-true", "cursor-not-allowed");
defaultTheme$1.set("disabled-false", "cursor-pointer");
var acsmTheme$1 = new Map();
acsmTheme$1.set("element", "rounded ease-out duration-500 bg-acsm-red accent-acsm-red disabled:bg-gray-400");
acsmTheme$1.set("container", "w-fit font-verdana");
acsmTheme$1.set("label", "ml-2 md:ml-3");
acsmTheme$1.set("disabled-true", "cursor-not-allowed");
acsmTheme$1.set("disabled-false", "cursor-pointer");
themes$1.set("", defaultTheme$1);
themes$1.set(CheckTheme.ACSMARINE, acsmTheme$1);
var CheckboxStyle = /** @class */ (function () {
    function CheckboxStyle() {
        this.theme = "";
        this.disabled = false;
        this.elementClassNames = "";
        this.appendedClassNames = "";
    }
    CheckboxStyle.prototype.buildElement = function () {
        var style = undefined;
        if (this.elementClassNames) {
            return this.elementClassNames;
        }
        var theme = themes$1.get(this.theme);
        if (!theme) {
            return style;
        }
        if (theme.has("element")) {
            style = theme.get("element");
        }
        if (theme.has("disabled-".concat(this.disabled))) {
            style += " " + theme.get("disabled-".concat(this.disabled));
        }
        if (this.appendedClassNames && this.appendedClassNames !== "") {
            style += " " + this.appendedClassNames;
        }
        return style;
    };
    CheckboxStyle.prototype.buildContainer = function () {
        var style = undefined;
        var theme = themes$1.get(this.theme);
        if (!theme) {
            return style;
        }
        if (theme.has("container")) {
            style = theme.get("container");
        }
        if (theme.has("disabled-".concat(this.disabled))) {
            style += " " + theme.get("disabled-".concat(this.disabled));
        }
        return style;
    };
    CheckboxStyle.prototype.buildLabel = function () {
        var style = undefined;
        var theme = themes$1.get(this.theme);
        if (!theme) {
            return style;
        }
        if (theme.has("label")) {
            style = theme.get("label");
        }
        if (theme.has("disabled-".concat(this.disabled))) {
            style += " " + theme.get("disabled-".concat(this.disabled));
        }
        return style;
    };
    CheckboxStyle.prototype.setTheme = function (theme) {
        this.theme = theme;
        return this;
    };
    CheckboxStyle.prototype.setDisabled = function (disabled) {
        this.disabled = disabled;
        return this;
    };
    CheckboxStyle.prototype.setElementClassNames = function (elementClassNames) {
        this.elementClassNames = elementClassNames;
        return this;
    };
    CheckboxStyle.prototype.setAppendedClassNames = function (appendedClassNames) {
        this.appendedClassNames = appendedClassNames;
        return this;
    };
    return CheckboxStyle;
}());
var styleBuilder$1 = new CheckboxStyle();
function Checkbox(props) {
    var themeCtx = React.useContext(ThemeContext);
    var _a = props.required, required = _a === void 0 ? false : _a, _b = props.disabled, disabled = _b === void 0 ? false : _b, _c = props.checked, checked = _c === void 0 ? false : _c, className = props.className, appendClassNames = props.appendClassNames;
    var _d = props.theme, theme = _d === void 0 ? themeCtx.theme || "" : _d;
    if (!themes$1.has(theme)) {
        theme = "";
    }
    styleBuilder$1
        .setTheme(theme)
        .setElementClassNames(className)
        .setAppendedClassNames(appendClassNames)
        .setDisabled(disabled);
    var containerClassNames = styleBuilder$1.buildContainer();
    var elementClassNames = styleBuilder$1.buildElement();
    var labelClassNames = styleBuilder$1.buildLabel();
    var handleInput = function (e) {
        props.onInput && props.onInput(e);
    };
    var handleChange = function (e) {
        props.onChange && props.onChange(e);
    };
    var handleBlur = function (e) {
        props.onBlur && props.onBlur(e);
    };
    return (React.createElement("div", { className: containerClassNames },
        React.createElement(Base, __assign({ type: "checkbox", id: props.id, name: props.name, checked: checked, required: required, disabled: disabled, onInput: handleInput, onChange: handleChange, onBlur: handleBlur, className: elementClassNames }, props)),
        props.label &&
            React.createElement("label", { htmlFor: props.id, className: labelClassNames }, props.label)));
}

var themes = new Map();
var defaultTheme = new Map();
defaultTheme.set("element", "rounded ease-out duration-500 bg-gray-900 accent-gray-900 disabled:bg-gray-400");
defaultTheme.set("container", "w-fit");
defaultTheme.set("label", "ml-2 md:ml-3");
defaultTheme.set("disabled-true", "cursor-not-allowed");
defaultTheme.set("disabled-false", "cursor-pointer");
var acsmTheme = new Map();
acsmTheme.set("element", "rounded ease-out duration-500 bg-acsm-red accent-acsm-red disabled:bg-gray-400");
acsmTheme.set("container", "w-fit font-verdana sm:!font-poppins");
acsmTheme.set("label", "ml-2 md:ml-3");
acsmTheme.set("disabled-true", "cursor-not-allowed");
acsmTheme.set("disabled-false", "cursor-pointer");
themes.set("", defaultTheme);
themes.set(CheckTheme.ACSMARINE, acsmTheme);
var RadioStyle = /** @class */ (function () {
    function RadioStyle() {
        this.theme = "";
        this.disabled = false;
        this.elementClassNames = "";
        this.appendedClassNames = "";
    }
    RadioStyle.prototype.buildElement = function () {
        var style = undefined;
        if (this.elementClassNames) {
            return this.elementClassNames;
        }
        var theme = themes.get(this.theme);
        if (!theme) {
            return style;
        }
        if (theme.has("element")) {
            style = theme.get("element");
        }
        if (theme.has("disabled-".concat(this.disabled))) {
            style += " " + theme.get("disabled-".concat(this.disabled));
        }
        if (this.appendedClassNames !== "") {
            style += " " + this.appendedClassNames;
        }
        return style;
    };
    RadioStyle.prototype.buildContainer = function () {
        var style = undefined;
        var theme = themes.get(this.theme);
        if (!theme) {
            return style;
        }
        if (theme.has("container")) {
            style = theme.get("container");
        }
        if (theme.has("disabled-".concat(this.disabled))) {
            style += " " + theme.get("disabled-".concat(this.disabled));
        }
        return style;
    };
    RadioStyle.prototype.buildLabel = function () {
        var style = undefined;
        var theme = themes.get(this.theme);
        if (!theme) {
            return style;
        }
        if (theme.has("label")) {
            style = theme.get("label");
        }
        if (theme.has("disabled-".concat(this.disabled))) {
            style += " " + theme.get("disabled-".concat(this.disabled));
        }
        return style;
    };
    RadioStyle.prototype.setTheme = function (theme) {
        this.theme = theme;
        return this;
    };
    RadioStyle.prototype.setDisabled = function (disabled) {
        this.disabled = disabled;
        return this;
    };
    RadioStyle.prototype.setElementClassNames = function (elementClassNames) {
        this.elementClassNames = elementClassNames;
        return this;
    };
    RadioStyle.prototype.setAppendedClassNames = function (appendedClassNames) {
        this.appendedClassNames = appendedClassNames;
        return this;
    };
    return RadioStyle;
}());
var styleBuilder = new RadioStyle();
function Radio(props) {
    var themeCtx = React.useContext(ThemeContext);
    var _a = props.required, required = _a === void 0 ? false : _a, _b = props.disabled, disabled = _b === void 0 ? false : _b, _c = props.checked, checked = _c === void 0 ? false : _c, className = props.className, appendClassNames = props.appendClassNames;
    var _d = props.theme, theme = _d === void 0 ? themeCtx.theme || "" : _d;
    if (!themes.has(theme)) {
        theme = "";
    }
    styleBuilder
        .setTheme(theme)
        .setAppendedClassNames(appendClassNames)
        .setElementClassNames(className)
        .setDisabled(disabled);
    var containerClassName = styleBuilder.buildContainer();
    var elementClassName = styleBuilder.buildElement();
    var labelClassName = styleBuilder.buildLabel();
    var handleInput = function (e) {
        props.onInput && props.onInput(e);
    };
    var handleChange = function (e) {
        props.onChange && props.onChange(e);
    };
    var handleBlur = function (e) {
        props.onBlur && props.onBlur(e);
    };
    return (React.createElement("div", { className: containerClassName },
        React.createElement(Base, __assign({ type: "radio", id: props.id, name: props.name, value: props.value, required: required, disabled: disabled, checked: checked, onInput: handleInput, onChange: handleChange, onBlur: handleBlur, className: elementClassName }, props)),
        props.label &&
            React.createElement("label", { htmlFor: props.id, className: labelClassName }, props.label)));
}

export { Checkbox, Radio };
